/**
 * Schémas Zod du module Tirage (Lot D) — partagés API ⇄ ERP.
 *
 * Le TIRAGE est la mise en bouteille pour prise de mousse : opération IMMUABLE
 * (trigger 0059), MONO-FORMAT, qui crée un lot `sur_lattes` + son mouvement
 * `entree_tirage`. Correction = annulation par opération inverse, jamais d'édition.
 * Source des énumérations/CHECKs : migration `0058_lot_d_cave.sql` (`tirages`).
 * Volumes en LITRES au contrat (le moteur `tirage-calculs.ts` travaille en hL).
 */

import { z } from "zod";

import {
  homogeneiteLiquideSchema,
  prelevementPositionLiquideSchema,
  versionStockLiquideSchema,
  volumeLiquideSchema,
} from "./liquides.js";
import { matierePoseeSchema } from "./matiere-posee.js";
import { idSchema } from "./parcellaire.js";
import { dateIsoSchema } from "./vendange.js";
import { VOLUME_L_MAX, volumeLitresSchema } from "./vins-clairs.js";

// ────────────────────────────────────────────────────────────────────────────
// Constantes métier NOMMÉES (uniques sources de vérité — jamais recodées)
// ────────────────────────────────────────────────────────────────────────────

/**
 * Garde-fou d'USAGE champenois du dosage de tirage (g/L de sucre sur le volume
 * tiré) : au-delà → ALERTE non bloquante « au-delà de l'usage ». Ce n'est PAS
 * la borne réglementaire. Défaut du paramètre tenant
 * `parametres_tirage.dosage_tirage_defaut_g_l` (paramétrable).
 */
export const DOSAGE_TIRAGE_USAGE_MAX_G_L = 24;

/**
 * Borne DURE UE de la liqueur de tirage (règl. (UE) 2019/934) : l'augmentation
 * du TAV total ne peut excéder +1,5 % vol (≈ 25 g/L de sucre). BLOQUANT côté
 * use case — le contrôle utilise cette constante, jamais un littéral.
 */
export const DELTA_TAV_MAX_LIQUEUR_TIRAGE_PCT = 1.5;

/**
 * Équivalence œnologique standard : ~16,83 g/L de sucre fermenté produisent
 * +1 % vol de TAV (constante PHYSIQUE de fermentation, pas une valeur tenant).
 * 1,5 % vol × 16,83 ≈ 25,2 g/L — cohérent avec le « ≈ 25 g/L » du cadrage D.2.
 * Source unique partagée API (invariants tirages) ⇄ ERP (écho client).
 */
export const SUCRE_G_L_PAR_PCT_TAV = 16.83;

/** Formats mousseux autorisés 12,5-150 cl (directive 2007/45/CE) ; > 150 cl libre. */
export const FORMATS_BOUTEILLE_CL = [12.5, 20, 37.5, 75, 150] as const;

/** Au-delà de ce seuil (cl), le format est LIBRE (grands formats : jéroboam…). */
export const FORMAT_CL_LIBRE_AU_DELA = 150;

/** Garde-fou de saisie : nb de bouteilles max par opération (40 ha ≈ 400 000 bt/an). */
export const NB_BOUTEILLES_MAX = 1_000_000;

/** Garde-fou de saisie des g/L (borne colonne NUMERIC(5,2)). */
export const DOSAGE_G_L_MAX = 999.99;

// ────────────────────────────────────────────────────────────────────────────
// Schémas atomiques partagés Lot D (réutilisés par dégorgement/reprise/stock)
// ────────────────────────────────────────────────────────────────────────────

/** Format bouteille en cl — miroir CHECK SQL (liste fermée 12,5-150, libre au-delà). */
export const formatClSchema = z
  .number({ invalid_type_error: "Format invalide.", required_error: "Format requis." })
  .positive("Le format doit être supérieur à 0.")
  .max(3000, "Format maximum : 30 L.")
  .refine(
    (valeur) =>
      (FORMATS_BOUTEILLE_CL as readonly number[]).includes(valeur) ||
      valeur > FORMAT_CL_LIBRE_AU_DELA,
    "Format non autorisé : 0,125 / 0,20 / 0,375 / 0,75 / 1,5 L, ou > 1,5 L (directive 2007/45/CE).",
  );

/** Nombre de bouteilles d'une opération : entier strictement positif, borné. */
export const nbBouteillesSchema = z
  .number({ invalid_type_error: "Quantité invalide.", required_error: "Quantité requise." })
  .int("La quantité doit être un nombre entier de bouteilles.")
  .positive("La quantité doit être supérieure à 0.")
  .max(NB_BOUTEILLES_MAX, `Quantité maximum : ${NB_BOUTEILLES_MAX} bouteilles.`);

/** Dosage en g/L de sucre : ≥ 0 (0 = non dosé), borné saisie. */
export const dosageGLSchema = z
  .number({ invalid_type_error: "Dosage invalide.", required_error: "Dosage requis." })
  .min(0, "Le dosage ne peut être négatif.")
  .max(DOSAGE_G_L_MAX, `Dosage maximum : ${DOSAGE_G_L_MAX} g/L.`);

/** Concentration d'une liqueur en g/L : strictement positive, bornée saisie. */
export const concentrationGLSchema = z
  .number({
    invalid_type_error: "Concentration invalide.",
    required_error: "Concentration requise.",
  })
  .positive("La concentration doit être supérieure à 0.")
  .max(DOSAGE_G_L_MAX, `Concentration maximum : ${DOSAGE_G_L_MAX} g/L.`);

/** Part de levain en % du volume (vin + liqueur) : 0-100. */
export const levainPctSchema = z
  .number({ invalid_type_error: "Levain invalide." })
  .min(0, "Le levain ne peut être négatif.")
  .max(100, "Le levain ne peut excéder 100 %.");

/** Volume physique du bilan de tirage : zéro autorisé, précision en litres. */
export const volumeBilanTirageSchema = z
  .number({ invalid_type_error: "Volume invalide.", required_error: "Volume requis." })
  .min(0, "Le volume ne peut être négatif.")
  .max(VOLUME_L_MAX, `Volume maximum : ${VOLUME_L_MAX} L (1000 hL).`);

/**
 * Complétude du bilan PHYSIQUE uniquement : `qualifie` signifie que les volumes
 * ferment arithmétiquement. Ce statut ne qualifie ni l'origine des matières ni
 * la conformité AOC du tirage. Aucun historique incomplet n'est reconstruit.
 */
export const bilanVolumeTirageStatutSchema = z.enum(["qualifie", "a_qualifier"]);
export type BilanVolumeTirageStatut = z.infer<typeof bilanVolumeTirageStatutSchema>;

/** Texte libre court optionnel — vide normalisé en undefined. */
const optionalShortText = (max: number, label: string) =>
  z
    .string()
    .trim()
    .max(max, `${label} trop long (${max} caractères max).`)
    .optional()
    .or(z.literal("").transform(() => undefined));

/** Notes libres (multi-ligne) optionnelles — vide normalisé en undefined. */
const notesSchema = z
  .string()
  .trim()
  .max(4000, "Notes trop longues (4000 caractères max).")
  .optional()
  .or(z.literal("").transform(() => undefined));

// ────────────────────────────────────────────────────────────────────────────
// Input — création d'un tirage (opération immuable)
// ────────────────────────────────────────────────────────────────────────────

/**
 * Input `tirage.creer` — enregistrer un tirage sur un assemblage VALIDÉ.
 *
 * Effets (transaction) : INSERT `tirages` + lot `sur_lattes` (dénormalisations
 * copiées) + mouvement `entree_tirage`. Invariants côté use case (données DB) :
 * date ≥ 01/01/(max millésime composants + 1) ; Σ tirages ≤ volume assemblage ;
 * ΔTAV ≤ +1,5 % vol (blocage) ; alerte au-delà du dosage d'usage tenant.
 */
export const tirageCreerInput = z
  .object({
    /** Paire de rejeu exigée après bascule ; le stock historique n'offre pas ce rejeu. */
    uuid_commande: z.string().uuid().optional(),
    version_bascule: z.string().min(1).max(100).optional(),
    /** Assemblage source (doit être au statut `valide`). */
    assemblage_id: idSchema,
    /** Date du tirage — point de départ des délais 12/15/36 mois. */
    date_tirage: dateIsoSchema,
    /** Vin d'assemblage mis en œuvre, en LITRES (consommation de l'assemblage). */
    volume_tire_l: volumeLitresSchema,
    /** Prélèvements physiques explicites, obligatoires après bascule du stock liquide. */
    sources_positions: z.array(prelevementPositionLiquideSchema).min(1).max(200).optional(),
    /** Préparations réellement incorporées ; leur fabrication ne se répète jamais au tirage. */
    preparations_positions: z
      .array(prelevementPositionLiquideSchema.extend({ preparation_id: idSchema }).strict())
      .max(100)
      .optional(),
    /** Volume réellement incorporé de liqueur de tirage, en LITRES. */
    volume_liqueur_tirage_l: volumeBilanTirageSchema,
    /** Volume réellement incorporé de levain de tirage, en LITRES. */
    volume_levain_l: volumeBilanTirageSchema,
    /** Pertes de tirage réellement mesurées, en LITRES (0 explicitement admis). */
    pertes_tirage_l: volumeBilanTirageSchema,
    /** Format unique du tirage, en cl (mono-format — N tirages pour N formats). */
    format_cl: formatClSchema,
    /** Nombre de bouteilles tirées (cohérence du bilan : blocage use case). */
    nb_bouteilles: nbBouteillesSchema,
    /** Dosage de la liqueur de tirage, g/L sur le volume tiré (optionnel). */
    dosage_tirage_g_l: dosageGLSchema.optional(),
    /** Concentration de la liqueur utilisée, g/L (optionnel). */
    concentration_liqueur_g_l: concentrationGLSchema.optional(),
    /** Part de levain, % du volume (optionnel). */
    levain_pct: levainPctSchema.optional(),
    /** N° de lot de tirage — absent = généré serveur (`T{millésime}-{seq}`), unique/domaine. */
    numero_lot: optionalShortText(40, "Numéro de lot"),
    /**
     * Intention de tirage couverte (Lot I D6) — posée à l'INSERT (table
     * immuable). Garde serveur : type='intention', statut='transmise' (ni
     * brouillon ni caduque), pas déjà couverte par un tirage VIVANT (un tirage
     * annulé libère l'intention — R3 ; anti-course par verrou FOR UPDATE).
     */
    declaration_intention_id: idSchema.optional(),
    /**
     * N° de lot fournisseur du VERRE consommé (traçabilité verre↔tirage, D7 —
     * réclamation casse en prise de mousse). Absent = lot fournisseur inconnu.
     */
    numero_lot_verre: optionalShortText(64, "N° de lot verre"),
    /** Une sélection remplace le décompte automatique du même type de matière. */
    bidules: matierePoseeSchema.optional(),
    capsules_tirage: matierePoseeSchema.optional(),
    /** Emplacement de cave initial du lot créé (plan 2D, optionnel). */
    emplacement_id: idSchema.optional(),
    /** Opérateur ayant réalisé la mise. */
    operateur_id: idSchema.optional(),
    /** Notes libres (lot de verre, mixtion, capsules…). */
    notes: notesSchema,
  })
  .superRefine((valeur, ctx) => {
    if ((valeur.uuid_commande === undefined) !== (valeur.version_bascule === undefined)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["uuid_commande"],
        message:
          "Le numéro de saisie et la version du suivi liquide doivent être fournis ensemble.",
      });
    }
    if (valeur.sources_positions) {
      const positions = new Set<string>();
      for (const [index, source] of valeur.sources_positions.entries()) {
        if (positions.has(source.position_id)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["sources_positions", index, "position_id"],
            message: "Une position de tirage ne peut être répétée.",
          });
        }
        positions.add(source.position_id);
      }
    }
    const preparations = valeur.preparations_positions ?? [];
    if (new Set(preparations.map((source) => source.position_id)).size !== preparations.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["preparations_positions"],
        message: "Une position de préparation ne peut être répétée.",
      });
    }
    // Une liqueur moins concentrée que le dosage visé ne peut pas doser
    // (le moteur renverrait 0 L de liqueur — erreur de saisie certaine).
    if (
      valeur.dosage_tirage_g_l !== undefined &&
      valeur.concentration_liqueur_g_l !== undefined &&
      valeur.concentration_liqueur_g_l <= valeur.dosage_tirage_g_l
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["concentration_liqueur_g_l"],
        message: "La concentration de la liqueur doit être supérieure au dosage visé.",
      });
    }
  });
export type TirageCreerInput = z.infer<typeof tirageCreerInput>;

/**
 * Motif OBLIGATOIRE d'annulation d'un tirage (arbitrage R3 Lot I) — il
 * décide du sort de la déclaration d'intention couverte :
 *  - `erreur_de_saisie`    : le tirage a bien eu lieu, on corrige les chiffres
 *    → l'intention redevient rattachable à un nouveau tirage ;
 *  - `tirage_non_realise`  : l'opération n'a pas eu lieu → l'intention devient
 *    CADUQUE (même transaction).
 */
export const motifAnnulationTirageSchema = z.enum(["erreur_de_saisie", "tirage_non_realise"], {
  required_error: "Motif d'annulation requis.",
  invalid_type_error: "Motif d'annulation invalide.",
});
export type MotifAnnulationTirage = z.infer<typeof motifAnnulationTirageSchema>;

/** Libellés courts des motifs (notes du registre + affichages compacts — DRY). */
export const MOTIF_ANNULATION_TIRAGE_LABELS: Record<MotifAnnulationTirage, string> = {
  erreur_de_saisie: "erreur de saisie",
  tirage_non_realise: "tirage non réalisé",
};

/**
 * Input `tirage.annuler` — annulation par OPÉRATION INVERSE (jamais d'UPDATE) :
 * mouvement `annulation` sur le mouvement d'entrée + lot passé `statut='annule'`.
 */
export const tirageAnnulerInput = z.object({
  /** Tirage à annuler (refusé si le lot a des mouvements aval). */
  id: idSchema,
  /** Motif d'annulation OBLIGATOIRE — sort de l'intention couverte (R3). */
  motif_annulation: motifAnnulationTirageSchema,
  /** Date du mouvement d'annulation (défaut : aujourd'hui côté serveur). */
  date_mouvement: dateIsoSchema.optional(),
  /** Précision libre du motif (tracée dans les notes du mouvement). */
  motif: optionalShortText(120, "Motif"),
});
export type TirageAnnulerInput = z.infer<typeof tirageAnnulerInput>;

// ────────────────────────────────────────────────────────────────────────────
// Input — lecture
// ────────────────────────────────────────────────────────────────────────────

/** Input `tirage.getById` — fiche détail d'un tirage. */
export const tirageGetByIdInput = z.object({ id: idSchema });
export type TirageGetByIdInput = z.infer<typeof tirageGetByIdInput>;

/** Input `tirage.lister` — filtres optionnels pour la liste tenant. */
export const tirageListerInput = z
  .object({
    assemblage_id: idSchema.optional(),
    cuvee_commerciale_id: idSchema.optional(),
  })
  .optional();
export type TirageListerInput = z.infer<typeof tirageListerInput>;

/** État des sources utilisables pour un tirage, sans inventer de localisation historique. */
export const tirageSourcesDisponiblesInput = z.object({ assemblage_id: idSchema });
export const tirageSourcesDisponiblesSortieSchema = z.object({
  mode_stock: z.enum(["positions", "historique"]),
  version_bascule: z.string().optional(),
  volume_disponible_l: volumeLiquideSchema,
  positions: z.array(
    z.object({
      position_id: idSchema,
      version: versionStockLiquideSchema,
      volume_l: volumeLiquideSchema,
      volume_disponible_l: volumeLiquideSchema,
      homogeneite: homogeneiteLiquideSchema,
      contenant_libelle: z.string(),
      type_contenant: z.enum(["cuve", "externe", "a_localiser"]),
      cuve_id: idSchema.nullable(),
    }),
  ),
});
export type TirageSourcesDisponiblesSortie = z.infer<typeof tirageSourcesDisponiblesSortieSchema>;

// ────────────────────────────────────────────────────────────────────────────
// Schémas de SORTIE (validation Zod en sortie de tRPC — defense in depth)
// ────────────────────────────────────────────────────────────────────────────

/**
 * Représentation d'un tirage renvoyée par l'API. NUMERIC → string (précision).
 * Les échéances AOC sont CALCULÉES serveur via `tirage-calculs.ts`
 * (`dateDegorgementMini` / `dateMiseEnMarcheMini`) — jamais recodées côté front.
 */
export const tirageSortieSchema = z.object({
  id: idSchema,
  assemblage_id: idSchema,
  /** Présents sur le détail : débits scellés au tirage, indépendants des soldes actuels. */
  prelevements_vin: z
    .array(
      z.object({
        position_id: idSchema,
        lot_id: idSchema,
        lot_libelle: z.string(),
        contenant_libelle: z.string(),
        volume_l: volumeLiquideSchema,
      }),
    )
    .optional(),
  /** Après bascule, l'annulation historique ne sait pas inverser un tirage physique. */
  suivi_matiere_actif: z.boolean().optional(),
  /** Présent sur le détail ; prélèvements réellement scellés, jamais reconstruits depuis la recette. */
  preparations_incorporees: z
    .array(
      z.object({
        preparation_id: idSchema,
        position_id: idSchema,
        libelle: z.string(),
        type_preparation: z.enum(["liqueur_tirage", "levain_tirage"]),
        contenant_libelle: z.string(),
        volume_l: volumeLiquideSchema,
      }),
    )
    .optional(),
  date_tirage: z.string(),
  volume_tire_l: z.string(),
  /** Null sur l'historique `a_qualifier`, mesuré sur les nouvelles saisies. */
  volume_liqueur_tirage_l: z.string().nullable(),
  volume_levain_l: z.string().nullable(),
  pertes_tirage_l: z.string().nullable(),
  /** Snapshot serveur du volume nominal conditionné et de l'écart signé. */
  volume_conditionne_l: z.string().nullable(),
  ecart_bilan_l: z.string().nullable(),
  bilan_volume_statut: bilanVolumeTirageStatutSchema,
  format_cl: z.string(),
  nb_bouteilles: z.number().int(),
  dosage_tirage_g_l: z.string().nullable(),
  concentration_liqueur_g_l: z.string().nullable(),
  levain_pct: z.string().nullable(),
  numero_lot: z.string(),
  operateur_id: idSchema.nullable(),
  notes: z.string().nullable(),
  cree_le: z.date(),
  /** Intention de tirage couverte (Lot I) — null si tirage sans intention. */
  declaration_intention_id: idSchema.nullable(),
  /** Millésime revendiqué par l'assemblage source (null = BSA). */
  millesime: z.number().int().nullable(),
  /** Vrai si l'assemblage source revendique un millésime (délai 36 mois). */
  est_millesime: z.boolean(),
  /** Première date de dégorgement autorisée (tirage + 12 mois). */
  date_degorgement_mini: dateIsoSchema.nullable(),
  /** Première date de mise en marché consommateur (tirage + 15 ou 36 mois). */
  date_mise_en_marche_mini: dateIsoSchema.nullable(),
});
export type TirageSortie = z.infer<typeof tirageSortieSchema>;

/** Sortie `tirage.lister` — liste de tirages. */
export const tirageListeSortieSchema = z.array(tirageSortieSchema);

/** Sortie `tirage.creer` — tirage + lot de bouteilles créé + échéances. */
export const tirageCreerSortieSchema = z.object({
  id: idSchema,
  numero_lot: z.string(),
  /** Lot de bouteilles `sur_lattes` créé par le tirage. */
  lot_bouteilles_id: idSchema,
  /** Mouvement `entree_tirage` (+nb) ancrant le solde dans le registre. */
  mouvement_id: idSchema,
  date_degorgement_mini: dateIsoSchema.nullable(),
  date_mise_en_marche_mini: dateIsoSchema.nullable(),
  cree_le: z.date(),
  /** Alerte non bloquante « dosage au-delà de l'usage tenant » (null si RAS). */
  alerte_dosage: z.string().nullable(),
  /**
   * Alerte non bloquante « stock matières sèches insuffisant » (null si RAS
   * ou si aucune nomenclature configurée) — le tirage est enregistré, le
   * stock passe en négatif, à régulariser (Lot I, 06-ux É4).
   */
  alerte_matieres_seches: z.string().nullable(),
});
export type TirageCreerSortie = z.infer<typeof tirageCreerSortieSchema>;

/** Sortie `tirage.annuler` — références de l'opération inverse. */
export const tirageAnnulerSortieSchema = z.object({
  tirage_id: idSchema,
  /** Lot créé par le tirage, passé `statut='annule'`. */
  lot_bouteilles_id: idSchema,
  /** Mouvement `annulation` créé (référence le mouvement d'entrée d'origine). */
  mouvement_annulation_id: idSchema,
});
export type TirageAnnulerSortie = z.infer<typeof tirageAnnulerSortieSchema>;
